import { Box, Button, Stack, Typography } from '@mui/material';
import {
  CellValueChangedEvent,
  ColDef,
  ColGroupDef,
  GridApi,
  GridReadyEvent,
  SelectionColumnDef,
  SizeColumnsToContentStrategy,
} from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC, useCallback, useMemo, useState } from 'react';
import { AgTable } from '../../../components/AgTable/AgTable';
import { FadeInGridWrapper } from '../../../components/grid-renderers/FadeInGridWrapper';
import {
  currentStepCapTableStateJ,
  sortedShareClassesStateJ,
} from '../../../services/state/CapTableFormStateJ';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { pluralizeWord } from '../../../util/stringUtils';
import { StepperFormButtons } from '../../Finance2/Forms/StepperFormButtons';
import { FormCard } from './commonStyledComponents';
import {
  EditableGridDefaultColDefs,
  EditableGridOptions,
  useEditableGridDefinitions,
} from './EditableGridDefinitions';
import { useCancelButtonProps } from './CapTableForm';
import { CaptableGridContainer } from './SummaryGrid/CaptableGridContainer';
import { useInvestmentRowData } from './useInvestmentRowData';
import { useCapTableSubtotals } from './useCapTableSubtotals';
import { isDataRow } from './captable-utils';
import { getUnissuedOptionsRow, UNISSUED_OPTIONS_ROW_LABEL } from './unissued-options-utils';

const minFundColWidth = 300;

export interface IAddInvestmentGridProps<T> {
  setStateToRows: (rows: T[]) => void;
  initialRowData: T[];
  colDefs: ColDef<T>[];
  HelpInfo?: FC;
}

const autoSizeStrategy: SizeColumnsToContentStrategy = { type: 'fitCellContents', skipHeader: false };

export function AddInvestmentsGrid<T>({
  setStateToRows,
  initialRowData,
  colDefs,
  HelpInfo,
}: IAddInvestmentGridProps<T>) {
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const setCurrentStep = useSetAtom(currentStepCapTableStateJ);
  const { rowData, nSelectedRows, onRowSelected, onAddRow, onDeleteRows } = useInvestmentRowData(
    initialRowData,
    gridApi
  );
  const { handleCellValueChanged } = useCapTableSubtotals(gridApi);
  const { isBetaFeature } = useFlags<MaggieFeatureFlags>();

  const modifiedColumnDefs = useMemo(() => {
    if (colDefs.length === 0) {
      return colDefs;
    }
    const [firstColDef, ...restColDefs] = colDefs;
    const labelledFirstColDef: ColDef<T> = {
      ...firstColDef,
      minWidth: minFundColWidth,
      pinned: 'left',
      ...(isBetaFeature ? { cellRenderer: withUnissuedOptionsRowLabel(firstColDef.cellRenderer) } : {}),
    };
    const defs = [labelledFirstColDef, ...restColDefs];
    return isBetaFeature ? defs.map(withReadonlyPinnedRows) : defs;
  }, [colDefs, isBetaFeature]);

  const shareClasses = useAtomValue(sortedShareClassesStateJ);
  const pinnedTopRowData = useMemo(() => {
    if (!isBetaFeature) {
      return undefined;
    }
    const unissuedOptionsRow = getUnissuedOptionsRow(shareClasses);
    return unissuedOptionsRow ? [unissuedOptionsRow as T] : undefined;
  }, [shareClasses, isBetaFeature]);

  const saveRows = useCallback(() => {
    if (!gridApi) {
      return Promise.resolve(false);
    }

    const rows: T[] = [];
    gridApi.forEachNode((node) => {
      // Exclude footer/total rows - they are calculated by ag-Grid, not part of actual data
      if (node.data && isDataRow(node)) {
        rows.push(node.data);
      }
    });

    setStateToRows(rows);

    return Promise.resolve(true);
  }, [gridApi, setStateToRows]);

  const cancelButtonProps = useCancelButtonProps();

  const onNext = useCallback(() => {
    return saveRows();
  }, [saveRows]);

  const onPrevious = useCallback(() => {
    saveRows();
    setCurrentStep((prev) => prev - 1);
  }, [saveRows, setCurrentStep]);

  const { processDataFromClipboard } = useEditableGridDefinitions();

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      setGridApi(params.api);
    },
    [setGridApi]
  );

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent) => {
      // Only handle changes to data rows, not footer/total rows
      if (!event.node || !isDataRow(event.node)) {
        return;
      }
      handleCellValueChanged();
    },
    [handleCellValueChanged]
  );

  return (
    <FormCard style={{ paddingRight: '1rem', minHeight: 'fit-content' }}>
      <Box minHeight={'50vh'} mb='1rem'>
        <Stack direction={'row'} justifyContent={'space-between'} mb='1rem'>
          {HelpInfo && <HelpInfo />}

          <Stack direction={'row'} gap='0.5rem'>
            {nSelectedRows > 0 && (
              <Button variant='outlined' color='secondary' onClick={onDeleteRows}>
                {`Delete ${pluralizeWord('Row', nSelectedRows)}`}
              </Button>
            )}
            <Button variant='contained' color='secondary' onClick={onAddRow}>
              Add Row
            </Button>
          </Stack>
        </Stack>
        <CaptableGridContainer style={{ width: '100%', height: '100%' }}>
          <FadeInGridWrapper>
            <AgTable
              onGridReady={onGridReady}
              columnDefs={modifiedColumnDefs}
              rowData={rowData}
              rowGroupPanelShow='never'
              gridOptions={EditableGridOptions}
              cellSelection={true}
              defaultColDef={EditableGridDefaultColDefs}
              processDataFromClipboard={processDataFromClipboard}
              onRowSelected={onRowSelected}
              onCellValueChanged={onCellValueChanged}
              autoSizeStrategy={autoSizeStrategy}
              selectionColumnDef={selectionColumnDef}
              grandTotalRow='bottom'
              pinnedTopRowData={pinnedTopRowData}
            />
          </FadeInGridWrapper>
        </CaptableGridContainer>
      </Box>
      <StepperFormButtons
        stepState={currentStepCapTableStateJ}
        stepIsValid={onNext}
        handleGoBack={onPrevious}
        secondaryActionProps={cancelButtonProps}
        style={{ position: 'unset' }}
      />
    </FormCard>
  );
}

const selectionColumnDef: SelectionColumnDef = {
  pinned: 'left',
  maxWidth: 50,
};

// The unissued-options pinned row mirrors the BE-managed "Stakeholder - TBD" allocation; it is
// edited via each option class's "Number of Unissued Options" field, not in this grid.
function withUnissuedOptionsRowLabel(originalCellRenderer: ColDef['cellRenderer']) {
  return function UnissuedOptionsRowLabel(params: CustomCellRendererProps) {
    if (params.node.rowPinned === 'top') {
      return (
        <Stack direction={'row'} alignItems={'center'} height={'100%'}>
          <Typography variant='body2' fontStyle={'italic'}>
            {UNISSUED_OPTIONS_ROW_LABEL}
          </Typography>
        </Stack>
      );
    }
    if (originalCellRenderer) {
      return originalCellRenderer(params);
    }
    return params.valueFormatted ?? params.value ?? null;
  };
}

function guardEditableForPinnedRows(editable: ColDef['editable']): ColDef['editable'] {
  return (params) => {
    if (params.node.rowPinned) {
      return false;
    }
    // Unset editable falls back to true, matching EditableGridDefaultColDefs.
    return typeof editable === 'function' ? editable(params) : (editable ?? true);
  };
}

function withReadonlyPinnedRows<T>(colDef: ColDef<T> | ColGroupDef<T>): ColDef<T> | ColGroupDef<T> {
  if ('children' in colDef) {
    return { ...colDef, children: colDef.children.map(withReadonlyPinnedRows) };
  }
  return { ...colDef, editable: guardEditableForPinnedRows(colDef.editable) };
}
